import { useEffect, useState } from "react";
import { X } from "lucide-react";
import DashboardLayout from "../../layouts/DashboardLayout";
import { api, money } from "../../api/client";

export default function OwnerReservations() {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);

  async function load() {
    setRows(await api("/api/owner/reservations"));
  }

  useEffect(() => {
    load().catch(() => setRows([]));
  }, []);

  async function setStatus(id, status) {
    const updated = await api(`/api/owner/reservations/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    });
    setRows((prev) => prev.map((row) => (row.id === id ? updated : row)));
    setSelected((current) => (current?.id === id ? updated : current));
  }

  return (
    <DashboardLayout title="Reservation book">
      <div className="overflow-x-auto card">
        <table className="min-w-full text-left text-sm">
          <thead className="text-xs uppercase tracking-[0.16em] text-ink/45">
            <tr>
              <th className="px-5 py-4">Guest</th>
              <th className="px-5 py-4">Occasion</th>
              <th className="px-5 py-4">When</th>
              <th className="px-5 py-4">Total</th>
              <th className="px-5 py-4">Status</th>
              <th className="px-5 py-4"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="border-t border-ink/5">
                <td className="px-5 py-4">
                  <p className="font-medium">{row.guestName}</p>
                  <p className="text-ink/50">{row.guestEmail}</p>
                </td>
                <td className="px-5 py-4">
                  {row.itemName}
                  <p className="text-ink/50">{row.guests} guests</p>
                </td>
                <td className="px-5 py-4">
                  {row.type === "event"
                    ? row.eventDate
                    : `${row.checkIn} → ${row.checkOut}`}
                </td>
                <td className="px-5 py-4">{money(row.total)}</td>
                <td className="px-5 py-4">
                  <select
                    className="field py-2"
                    value={row.status}
                    onChange={(e) => setStatus(row.id, e.target.value)}
                  >
                    <option value="pending">pending</option>
                    <option value="confirmed">confirmed</option>
                    <option value="cancelled">cancelled</option>
                  </select>
                </td>
                <td className="px-5 py-4">
                  <button className="btn-ghost" onClick={() => setSelected(row)}>
                    Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!rows.length && <p className="px-5 py-8 text-ink/50">No bookings yet.</p>}
      </div>

      {selected && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-ink/50 p-4"
          onClick={() => setSelected(null)}
        >
          <div
            className="card max-h-[90vh] w-full max-w-lg overflow-y-auto p-6"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-rose">Reservation</p>
                <h2 className="font-serif text-3xl">{selected.guestName}</h2>
              </div>
              <button
                className="grid h-10 w-10 place-items-center rounded-full bg-cream"
                onClick={() => setSelected(null)}
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>

            <div className="mt-6 space-y-3">
              <Detail label="Guest name" value={selected.guestName} />
              <Detail label="Email" value={selected.guestEmail} />
              <Detail label="Phone" value={selected.guestPhone} />
              <Detail label="Venue" value={selected.venueName} />
              <Detail
                label="Type"
                value={selected.type === "event" ? "Wedding / party" : "Hotel stay"}
              />
              <Detail label="Room / package" value={selected.itemName} />
              <Detail label="Guests" value={selected.guests} />
              {selected.type === "event" ? (
                <Detail label="Event date" value={selected.eventDate} />
              ) : (
                <>
                  <Detail label="Check-in" value={selected.checkIn} />
                  <Detail label="Check-out" value={selected.checkOut} />
                </>
              )}
              <Detail label="Total" value={money(selected.total)} />
              <Detail label="Status" value={selected.status} />
              <Detail label="Notes" value={selected.notes || "—"} />
              <Detail
                label="Requested"
                value={
                  selected.createdAt
                    ? new Date(selected.createdAt).toLocaleString()
                    : "—"
                }
              />
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

function Detail({ label, value }) {
  return (
    <div className="rounded-2xl bg-cream px-4 py-3">
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-ink/40">
        {label}
      </p>
      <p className="mt-1 break-words text-sm text-ink">{value || "—"}</p>
    </div>
  );
}
